import { isArray, isPlainObject, snakeCase } from "lodash-es";

import Logger from "./logger";

// Constants
const DEFAULT_MAX_DEPTH = 20;
const CIRCULAR_REFERENCE_ERROR = "Circular reference detected";
const MAX_DEPTH_EXCEEDED_ERROR = "Maximum depth exceeded";
const CONVERSION_FAILED_ERROR = "toSnakeCase conversion failed";

// Types
interface ConversionOptions {
  preserveSpecialObjects?: boolean;
  maxDepth?: number;
  specialConversion?: Record<string, string>;
}

interface ToSnakeCaseParams {
  object: unknown;
  options?: ConversionOptions;
  visited?: WeakSet<object>;
  parentField?: string;
  depth?: number;
}

interface ProcessArrayParams {
  array: unknown[];
  options: ConversionOptions;
  parentField: string;
  visited: WeakSet<object>;
  depth: number;
}

interface ProcessPlainObjectParams {
  obj: Record<string, unknown>;
  parentField: string;
  options: ConversionOptions;
  visited: WeakSet<object>;
  depth: number;
}

// Utility functions
const getParentField = (parentField: string, field: string): string => {
  return parentField ? `${parentField}.${field}` : field;
};

const getConvertedKey = (key: string, currentPath: string, specialConversion?: Record<string, string>): string => {
  if (!specialConversion) {
    return snakeCase(key);
  }

  // Build the full path for this key
  const fullPath = currentPath ? `${currentPath}.${key}` : key;

  if (specialConversion[fullPath]) {
    return specialConversion[fullPath];
  }

  if (specialConversion[key]) {
    return specialConversion[key];
  }

  // Default to snakeCase
  return snakeCase(key);
};

const shouldFlattenForSpecialConversion = (
  obj: Record<string, unknown>,
  currentPath: string,
  specialConversion?: Record<string, string>
): { shouldFlatten: boolean; flattenedResult?: Record<string, unknown> } => {
  if (!specialConversion) {
    return { shouldFlatten: false };
  }

  const flattenedResult: Record<string, unknown> = {};
  let hasFlattening = false;

  for (const [key, value] of Object.entries(obj)) {
    const fullPath = currentPath ? `${currentPath}.${key}` : key;

    // Check if this exact path has a special conversion
    const specialKey = specialConversion[fullPath];
    if (specialKey) {
      flattenedResult[specialKey] = value;
      hasFlattening = true;
    } else {
      // Use normal conversion for non-special paths
      const convertedKey = getConvertedKey(key, currentPath, specialConversion);
      flattenedResult[convertedKey] = value;
    }
  }

  return { shouldFlatten: hasFlattening, flattenedResult };
};

const isValidInput = (object: unknown, depth: number, maxDepth: number): boolean => {
  if (!object || typeof object !== "object") {
    return false;
  }

  if (depth > maxDepth) {
    handleWarning(MAX_DEPTH_EXCEEDED_ERROR);
    return false;
  }

  return true;
};

const shouldPreserveSpecialObject = (object: unknown, preserve = true): boolean => {
  if (!preserve) {
    return false;
  }

  return (
    object instanceof Date ||
    object instanceof RegExp ||
    object instanceof Map ||
    object instanceof Set ||
    (typeof globalThis.Buffer !== "undefined" && globalThis.Buffer.isBuffer?.(object) === true)
  );
};

const handleWarning = (message: string): void => {
  if (globalThis.process?.env.NODE_ENV === "development") {
    Logger.warn(message, { context: "toSnakeCase" });
  }
};

const handleError = (error: unknown, object: unknown, depth: number): void => {
  const errorInfo = {
    error: error instanceof Error ? error.message : String(error),
    objectType: typeof object,
    depth,
  };

  Logger.error(CONVERSION_FAILED_ERROR, {
    context: "toSnakeCase",
    data: errorInfo,
  });
};

// Processing functions
const processArray = ({ array, options, parentField, visited, depth }: ProcessArrayParams): unknown[] => {
  const result = array.map((item: unknown) =>
    toSnakeCase({
      object: item,
      options,
      visited,
      parentField,
      depth: depth + 1,
    })
  );

  visited.delete(array as object);
  return result;
};

const processPlainObject = ({
  obj,
  options,
  parentField,
  visited,
  depth,
}: ProcessPlainObjectParams): Record<string, unknown> => {
  // Check if we need special flattening for this object
  const { shouldFlatten, flattenedResult } = shouldFlattenForSpecialConversion(
    obj,
    parentField,
    options.specialConversion
  );

  if (shouldFlatten && flattenedResult) {
    // Process the flattened result recursively for nested objects
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(flattenedResult)) {
      if (typeof value === "function") {
        result[key] = value;
        continue;
      }

      // For already converted keys, don't apply further path-based conversion
      result[key] = toSnakeCase({
        object: value,
        options: {
          ...options,
          // Remove path-based conversions for already processed items
          specialConversion: undefined,
        },
        visited,
        parentField: getParentField(parentField, key),
        depth: depth + 1,
      });
    }

    visited.delete(obj as object);
    return result;
  }

  // Standard processing
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    const currentParentField = getParentField(parentField, key);
    const convertedKey = getConvertedKey(key, parentField, options.specialConversion);

    // Skip function properties - preserve them as-is
    if (typeof value === "function") {
      result[convertedKey] = value;
      continue;
    }

    result[convertedKey] = toSnakeCase({
      object: value,
      options,
      visited,
      parentField: currentParentField,
      depth: depth + 1,
    });
  }

  visited.delete(obj as object);
  return result;
};

// Main conversion function
export const toSnakeCase = <TResponse = unknown>({
  object,
  options = {
    preserveSpecialObjects: false,
    maxDepth: DEFAULT_MAX_DEPTH,
    specialConversion: {},
  },
  visited = new WeakSet<object>(),
  parentField = "",
  depth = 0,
}: ToSnakeCaseParams): TResponse => {
  const { maxDepth = DEFAULT_MAX_DEPTH } = options;

  // Early validation
  if (!isValidInput(object, depth, maxDepth)) {
    return object as TResponse;
  }

  // Circular reference detection
  if (visited.has(object as object)) {
    handleWarning(CIRCULAR_REFERENCE_ERROR);
    return object as TResponse;
  }

  try {
    visited.add(object as object);

    // Handle special object types
    if (shouldPreserveSpecialObject(object, options.preserveSpecialObjects)) {
      visited.delete(object as object);
      return object as TResponse;
    }

    // Process arrays
    if (isArray(object)) {
      return processArray({
        array: object,
        options,
        parentField,
        visited,
        depth,
      }) as TResponse;
    }

    // Process plain objects
    if (isPlainObject(object)) {
      return processPlainObject({
        obj: object as Record<string, unknown>,
        options,
        parentField,
        visited,
        depth,
      }) as TResponse;
    }

    // Return non-plain objects as-is
    visited.delete(object as object);
    return object as TResponse;
  } catch (error) {
    visited.delete(object as object);
    handleError(error, object, depth);
    return object as TResponse;
  }
};

// Convenience functions
export const toSnakeCaseSimple = <TResponse = unknown>(object: unknown): TResponse => {
  return toSnakeCase({
    object,
    options: {
      preserveSpecialObjects: true,
      maxDepth: DEFAULT_MAX_DEPTH,
    },
  });
};

export const toSnakeCaseForced = <TResponse = unknown>(
  object: unknown,
  customOptions: Partial<ConversionOptions> = {}
): TResponse => {
  return toSnakeCase({
    object,
    options: {
      preserveSpecialObjects: false,
      maxDepth: Number.MAX_SAFE_INTEGER,
      ...customOptions,
    },
  });
};
